import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import {
	type DevDocsBylineConnectionAncestor as DevDocsBylineConnectionAncestorType,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { getDevDocsProviderIcon } from '@confluence/dev-docs/entry-points/DevDocsProviderIcon.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { expVal } from '@confluence/feature-experiments';

import { ANALYTICS_SOURCE } from './analytics.ts';
import { BylineConnectionIconStack } from './BylineConnectionIconStack.tsx';
import { ConnectionsByLineMenu } from './ConnectionsByLineMenu.tsx';
import { useIncomingLinks } from './hooks/useIncomingLinks.ts';
import { useSmartLinks } from './hooks/useSmartLinks.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const i18n = defineMessages({
	connections: {
		id: 'byline-connections.label',
		defaultMessage: 'Connections',
		description: 'Label for the connections byline item shown in the page byline area',
	},
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
	teamworkGraph: {
		id: 'byline-connections.teamwork-graph-label',
		defaultMessage: 'Teamwork Graph',
		description: 'Label for the Teamwork Graph byline item shown in the page byline area',
	},
	tooltip: {
		id: 'byline-connections.tooltip',
		defaultMessage: 'See connected apps and links',
		description:
			'Tooltip shown on hover over the connections byline item explaining what Teamwork Graph is',
	},
	tooltipTwg: {
		id: 'byline-connections.tooltip-twg-v2',
		defaultMessage: "See how this content connects to your team's work",
		description:
			"Tooltip shown on hover over the connections byline item, describing that it shows how the current page connects to the team's work.",
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		// The byline row is `white-space: nowrap`; the top-layer tooltip renders
		// inside this subtree and inherits it, overflowing its box. CCINT-8744
		whiteSpace: 'normal',
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		cursor: 'pointer',
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});
/**
 * Forwards a node to a React ref that may be either a callback ref or an object
 * ref. Exported for unit testing so both ref shapes are covered.
 */
export const assignRef = (
	ref: React.Ref<HTMLButtonElement>,
	node: HTMLButtonElement | null,
): void => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
	}
};

/**
 * Resolves the byline tooltip text. Returns an empty string while the menu is
 * open (the tooltip is suppressed then), otherwise the experiment-gated label.
 * Reading `expVal` only when the tooltip is shown keeps exposure firing to the
 * cases where the copy is actually visible.
 */
const getTooltipContent = (isOpen: boolean, formatMessage: IntlShape['formatMessage']): string => {
	if (isOpen) {
		return '';
	}
	return formatMessage(
		expVal('1p_3p_connections_byline_twg', 'isEnabled', false) ? i18n.tooltipTwg : i18n.tooltip,
	);
};

type BylineConnectionsProps = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
};

export type DevDocsBylineConnectionAncestor = DevDocsBylineConnectionAncestorType;

const BylineConnectionsInner: FC<BylineConnectionsProps> = ({ contentId, connectionAncestors }) => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);
	// The popup's focus trap returns focus to the trigger on close by calling
	// `.focus()` (without `preventScroll`), which scrolls the trigger back into
	// view. For a link navigation that fights the in-page scroll, so we disable
	// the trap's focus return (`shouldReturnFocus={false}`) and instead restore
	// focus ourselves on non-navigation closes (Esc / outside click) using
	// `preventScroll` — keeping keyboard a11y without the scroll jump.
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const navigatedRef = useRef(false);
	const hasFiredViewedRef = useRef(false);

	const { groups, loading, links, refreshLinkUrl } = useSmartLinks();
	const { links: incomingLinks } = useIncomingLinks(contentId);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const devDocsIcons = useMemo(() => {
		const devDocsProviderIcon = getDevDocsProviderIcon(devDocsConnection);
		return devDocsProviderIcon ? [devDocsProviderIcon] : undefined;
	}, [devDocsConnection]);

	// Fire a single "viewed" event the first time the byline actually renders its
	// content — skip while loading and while there is nothing to show (both cases
	// early-return before the trigger is visible).
	useEffect(() => {
		if (hasFiredViewedRef.current || loading || (links.length === 0 && !devDocsConnection)) {
			return;
		}
		hasFiredViewedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'byline',
				actionSubjectId: 'connectionsByline',
				source: ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent, loading, links.length, devDocsConnection]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		// Esc / outside-click close: return focus to the trigger without scrolling.
		if (!navigatedRef.current) {
			triggerRef.current?.focus({ preventScroll: true });
		}
	}, []);
	const handleNavigate = useCallback(() => {
		navigatedRef.current = true;
		setIsOpen(false);
	}, []);
	const handleToggle = useCallback(() => {
		if (!isOpen) {
			navigatedRef.current = false;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'connectionsByline',
					source: ANALYTICS_SOURCE,
				},
			}).fire();
		}

		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen]);

	const handleItemHover = useCallback((item: EnhancedSmartLink) => {
		// eslint-disable-next-line no-console
		console.debug('[BylineConnections] hover', item.ari);
	}, []);

	if (loading) {
		return (
			<span css={styles.wrapper} role="status" aria-label={formatMessage(i18n.loadingConnections)}>
				<Skeleton width={100} height={20} testId="byline-connections-loading-skeleton" />
			</span>
		);
	}

	if (links.length === 0 && !devDocsConnection) {
		return null;
	}

	return (
		<span css={styles.wrapper}>
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				placement="bottom-start"
				shouldReturnFocus={false}
				content={() => (
					<ConnectionsByLineMenu
						isOpen={isOpen}
						groups={groups}
						links={links}
						incomingLinks={incomingLinks}
						devDocsConnection={devDocsConnection}
						contentId={contentId}
						showFooter={links.length > 0}
						refreshLinkUrl={refreshLinkUrl}
						onItemHover={handleItemHover}
						onNavigate={handleNavigate}
					/>
				)}
				trigger={({ ref, ...triggerProps }: TriggerProps) => (
					<Tooltip content={getTooltipContent(isOpen, formatMessage)}>
						{({ ref: tooltipRef, onClick: onTooltipClick, ...tooltipProps }) => (
							<Pressable
								{...tooltipProps}
								{...triggerProps}
								ref={(node: HTMLButtonElement | null) => {
									triggerRef.current = node;
									assignRef(ref, node);
									tooltipRef(node);
								}}
								xcss={styles.trigger}
								onClick={(event) => {
									onTooltipClick(event);
									handleToggle();
								}}
								aria-expanded={isOpen}
								aria-haspopup="dialog"
							>
								<BylineConnectionIconStack links={links} additionalIcons={devDocsIcons} />
								{formatMessage(i18n.teamworkGraph)}
							</Pressable>
						)}
					</Tooltip>
				)}
			/>
		</span>
	);
};

export const BylineConnections: (props: any) => React.JSX.Element =
	withSmartCardProviderContext(BylineConnectionsInner);
